import React, { FC, useEffect, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";

import { css, ThemeProvider } from "@emotion/react";
import {
  DiscountQuantityWidgetProps,
  DiscountQuantityWidgetTheme,
  WidgetPrefixes,
} from "@intelligems/ig-types";
import { DiscountQuantityButtonType } from "@intelligems/ig-types/src/dtos/deprecated/discounts";

import { DiscountQuantityContext } from "../context";
import { FullIgTheme, IgWidgetTheme } from "../theme";

import { ButtonRoot } from "./Buttons/ButtonRoot";

export const DiscountQuantityWidgetBase: FC<DiscountQuantityWidgetProps> = ({
  windowState,
  currencyFormatter,
  handleButtonClick,
  getWidgetConfig,
  config,
  isMobile,
  prefix,
  handleMessage,
  onStart,
}) => {
  const prefixes: WidgetPrefixes | undefined = useMemo(() => {
    if (prefix) {
      return {
        "ig-dq": prefix,
      };
    }
    return undefined;
  }, [prefix]);

  const getTheme = () => {
    if (isMobile) {
      return IgWidgetTheme(
        config.mobile?.variant || config.desktop!.variant!,
        true,
        prefixes
      ) as DiscountQuantityWidgetTheme;
    }
    return IgWidgetTheme(
      config.desktop!.variant!,
      false,
      prefixes
    ) as DiscountQuantityWidgetTheme;
  };

  const [theme, setTheme] = useState(getTheme());

  useEffect(() => {
    setTheme(getTheme());
  }, [prefixes, config.desktop?.variant, config.mobile?.variant, isMobile]);

  useEffect(() => {
    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  useEffect(() => {
    void (async () => {
      if (onStart) {
        onStart();
      }
    })();

    return () => {};
  }, []);

  const containerClass = useMemo(
    () => css`
      font-family: ${theme.container.font.family};
      margin: ${theme.container.margin};
      display: flex;
      justify-content: center;
      align-items: center;
      flex-wrap: wrap;
      gap: ${theme.container.gap};
      @media only screen and (max-width: 600px) {
        max-width: 100%;
      }
      @media only screen and (min-width: 601px) {
        max-width: 455px;
      }
    `,
    [theme]
  );

  return (
    <ThemeProvider theme={FullIgTheme(isMobile, prefixes)}>
      <DiscountQuantityContext.Provider
        value={{
          config,
          prefixes,
          windowState,
          theme,
          isMobile,
          getWidgetConfig,
          handleButtonClick,
          currencyFormatter,
        }}
      >
        <div className={`ig-volume-widget-container`} css={containerClass}>
          {/* @ts-ignore */}
          {(getWidgetConfig("variables.buttons") || [])
            .sort(
              (
                a: DiscountQuantityButtonType,
                b: DiscountQuantityButtonType
              ) => {
                if (a.quantity === b.quantity) {
                  return 0;
                }
                return a.quantity > b.quantity ? 1 : -1;
              }
            )
            .map((button: DiscountQuantityButtonType, index: number) => {
              return (
                <ButtonRoot
                  key={`ig-button-${index}-${button.quantity}`}
                  button={button}
                  index={index}
                />
              );
            })}
        </div>
      </DiscountQuantityContext.Provider>
    </ThemeProvider>
  );
};

export { createRoot };
